import { useState, useCallback, useMemo } from 'react';

/**
 * useSelection - Headless Selection Hook
 * 專門處理列表的選取邏輯
 *
 * @param {Array} list - 當前頁面的數據列表
 * @param {string} idKey - 數據的唯一標識鍵，預設為 'id'
 */
const useSelection = (list = [], idKey = 'id') => {
	// 使用 Set 來存儲選中的 ID，確保效能
	const [selectedIds, setSelectedIds] = useState(new Set());

	// 切換單一項目的選取狀態
	const toggleSelection = useCallback((id) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) {
				next.delete(id);
			} else {
				next.add(id);
			}
			return next;
		});
	}, []);

	// 全選/取消全選當前頁面
	const isAllSelected = useMemo(() => {
		if (list.length === 0) return false;
		return list.every((item) => selectedIds.has(item[idKey]));
	}, [list, selectedIds, idKey]);

	const toggleSelectAll = useCallback(() => {
		if (isAllSelected) {
			// 取消全選當前頁面的項目
			setSelectedIds((prev) => {
				const next = new Set(prev);
				list.forEach((item) => next.delete(item[idKey]));
				return next;
			});
		} else {
			// 全選當前頁面的項目
			setSelectedIds((prev) => {
				const next = new Set(prev);
				list.forEach((item) => next.add(item[idKey]));
				return next;
			});
		}
	}, [isAllSelected, list, idKey]);

	// 清空所有選取
	const clearSelection = useCallback(() => {
		setSelectedIds(new Set());
	}, []);

	// 獲取選中的完整數據物件
	const selectedItems = useMemo(() => {
		// 注意：這裡可能只能獲取到目前已載入到 list 中的數據
		// 如果是跨頁選取，需要更複雜的數據管理
		return list.filter((item) => selectedIds.has(item[idKey]));
	}, [list, selectedIds, idKey]);

	return {
		selectedIds,
		selectedCount: selectedIds.size,
		selectedItems,
		isAllSelected,
		toggleSelection,
		toggleSelectAll,
		clearSelection,
	};
};

export default useSelection;
